<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="handleBackdropClick"
    >
      <div class="bg-white rounded shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 class="font-semibold text-gray-900">
            {{ mode === 'reply' ? 'Reply' : 'New Message' }}
          </h3>
          <button class="btn-icon" @click="handleBackdropClick">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          <!-- Patient -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-1">
              {{ lockedPatient ? 'Patient' : 'Patient (optional)' }}
            </label>
            <p class="text-[11px] text-gray-500 mb-1.5">
              Links this message to a patient record internally — not shared with the provider.
            </p>
            <!-- Locked patient -->
            <div v-if="lockedPatient" class="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
              {{ lockedPatient.name }}
            </div>
            <!-- Selectable patient -->
            <template v-else>
              <div v-if="selectedPatientId" class="flex items-center gap-2">
                <div class="flex-1 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                  {{ selectedPatientName }}
                </div>
                <button
                  type="button"
                  class="btn-icon text-xs"
                  @click="clearPatient"
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </button>
              </div>
              <div v-else class="relative">
                <input
                  v-model="patientQuery" data-demo-search
                  type="text"
                  placeholder="Search by patient name…"
                  class="sensitive w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  @input="onPatientInput"
                />
                <ul
                  v-if="patientResults.length"
                  class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto"
                >
                  <li
                    v-for="entity in patientResults"
                    :key="entity.id"
                    class="px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-3"
                    @mousedown.prevent="selectPatient(entity)"
                  >
                    <span class="font-medium text-gray-800 sensitive">{{ entity.name }}</span>
                    <span v-if="entity.identifiers?.BENE_MBI_ID" class="text-gray-400 text-xs shrink-0 sensitive">{{ entity.identifiers.BENE_MBI_ID }}</span>
                  </li>
                </ul>
                <div v-if="patientLoading" class="absolute right-3 top-2.5 text-gray-400 text-xs">
                  <font-awesome-icon :icon="['fas', 'circle-notch']" spin />
                </div>
              </div>
            </template>
          </div>

          <!-- Recipient -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-1">To *</label>

            <!-- Reply mode: locked recipient -->
            <div
              v-if="mode === 'reply'"
              class="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700"
            >
              <div v-if="toDisplayName" class="font-medium text-gray-800">{{ toDisplayName }}</div>
              <div :class="toDisplayName ? 'text-xs text-gray-500' : ''">{{ toEmail }}</div>
            </div>

            <!-- New mode: selected recipient chip, or the picker -->
            <template v-else>
              <template v-if="toEmail">
                <div class="flex items-center gap-2">
                  <div class="flex-1 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary truncate">
                    {{ toDisplayName || toEmail }}
                  </div>
                  <button
                    type="button"
                    class="btn-icon text-xs"
                    @click="clearRecipient"
                  >
                    <font-awesome-icon :icon="['fas', 'xmark']" />
                  </button>
                </div>
                <div v-if="toDisplayName" class="text-xs text-gray-500 mt-1 truncate">{{ toEmail }}</div>
              </template>
              <button
                v-else
                type="button"
                class="btn-secondary w-full text-left"
                @click="showRecipientPicker = true"
              >
                <font-awesome-icon :icon="['fas', 'user-plus']" class="mr-1.5" />
                Choose a recipient
              </button>
            </template>
          </div>

          <!-- Subject -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-1">Subject *</label>
            <div
              v-if="mode === 'reply'"
              class="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700"
            >
              {{ subject }}
            </div>
            <input
              v-else
              v-model="subject"
              type="text"
              placeholder="Subject"
              class="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            />
          </div>

          <!-- Body -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-1">Message *</label>
            <textarea
              v-model="body"
              rows="6"
              class="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-y"
              placeholder="Write your message…"
            />
          </div>

          <!-- Attachments -->
          <div>
            <input ref="fileInput" type="file" multiple class="hidden" @change="onAttachmentChange" />
            <button type="button" class="btn-secondary text-xs" @click="$refs.fileInput.click()">
              <font-awesome-icon :icon="['fas', 'paperclip']" class="mr-1.5" />
              Attach files
            </button>
            <div v-if="attachments.length" class="flex flex-wrap gap-1.5 mt-2">
              <span
                v-for="(file, i) in attachments"
                :key="i"
                class="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full pl-2.5 pr-1 py-0.5"
              >
                <font-awesome-icon :icon="['fas', 'file']" class="text-[10px]" />
                {{ file.name }}
                <button type="button" class="ml-0.5 rounded-full hover:bg-blue-200 p-0.5" @click="attachments.splice(i, 1)">
                  <font-awesome-icon :icon="['fas', 'xmark']" class="text-[10px]" />
                </button>
              </span>
            </div>
          </div>

          <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button class="btn-secondary" :disabled="sending" @click="$emit('update:modelValue', false)">Cancel</button>
          <button
            class="btn-primary"
            :disabled="sending || !canSend"
            @click="submit"
          >
            <font-awesome-icon v-if="sending" :icon="['fas', 'circle-notch']" spin class="mr-1" />
            {{ sending ? 'Sending…' : 'Send' }}
          </button>
        </div>
      </div>

      <teleport to="body">
        <div
          v-if="showRecipientPicker"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          @click.self="showRecipientPicker = false"
        >
          <div class="bg-white rounded shadow-xl w-full max-w-xl mx-4 flex flex-col h-[75vh] max-h-[42rem]">
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 class="font-semibold text-gray-900">Choose a Recipient</h3>
              <button class="btn-icon" @click="showRecipientPicker = false">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
            <div class="flex-1 overflow-y-auto p-5">
              <RecipientPicker :waterfall-context="waterfallContext" @select="selectRecipient" />
            </div>
          </div>
        </div>
      </teleport>
    </div>
  </teleport>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as messagingApi from '@/services/api/providerMessaging.js';
import RecipientPicker from '@/components/shared/RecipientPicker.vue';
import demoProtectedFields from '@/mixins/demoProtectedFields';

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default {
  name: 'ComposeMessageModal',
  components: { FontAwesomeIcon, RecipientPicker },
  mixins: [demoProtectedFields],
  // The patient-name search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['patientQuery'],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'new', // 'new' | 'reply'
    },
    // When set, patient field is locked to this patient
    lockedPatient: {
      type: Object, // { id: number, name: string }
      default: null,
    },
    initialToEmail: {
      type: String,
      default: '',
    },
    initialToDisplayName: {
      type: String,
      default: '',
    },
    initialSubject: {
      type: String,
      default: '',
    },
    initialBody: {
      type: String,
      default: '',
    },
    // Initial patient for compose (from parent's state, e.g. linked message patient)
    initialPatientId: {
      type: Number,
      default: null,
    },
    initialPatientName: {
      type: String,
      default: '',
    },
    waterfallContext: {
      type: Object,
      default: null,
    },
    // Optional owner-specific send path. The default continues to use the
    // provider-messaging endpoint; a workflow that must link the send to its
    // own record (for example a clinical recommendation review) can supply an
    // equivalent async handler instead.
    onSend: {
      type: Function,
      default: null,
    },
  },

  emits: ['update:modelValue', 'sent'],

  data() {
    return {
      toEmail: this.initialToEmail,
      toDisplayName: this.initialToDisplayName,
      subject: this.initialSubject,
      body: this.initialBody,
      attachments: [],
      sending: false,
      error: '',

      // Patient search (only used when lockedPatient is null)
      selectedPatientId: this.initialPatientId,
      selectedPatientName: this.initialPatientName,
      patientQuery: '',
      patientResults: [],
      patientLoading: false,
      patientTimer: null,
      showRecipientPicker: false,
    };
  },

  computed: {
    canSend() {
      return this.toEmail.trim() && this.subject.trim() && this.body.trim();
    },
    effectivePatientId() {
      return this.lockedPatient ? this.lockedPatient.id : this.selectedPatientId;
    },
  },

  watch: {
    modelValue(val) {
      if (val) this.reset();
    },
    initialToEmail(val) { this.toEmail = val; },
    initialToDisplayName(val) { this.toDisplayName = val; },
    initialSubject(val) { this.subject = val; },
    initialBody(val) { this.body = val; },
    initialPatientId(val) { this.selectedPatientId = val; },
    initialPatientName(val) { this.selectedPatientName = val; },
  },

  methods: {
    reset() {
      this.toEmail = this.initialToEmail;
      this.toDisplayName = this.initialToDisplayName;
      this.subject = this.initialSubject;
      this.body = this.initialBody;
      this.attachments = [];
      this.sending = false;
      this.error = '';
      this.selectedPatientId = this.initialPatientId;
      this.selectedPatientName = this.initialPatientName;
      this.patientQuery = '';
      this.patientResults = [];
      this.showRecipientPicker = false;
    },

    // ── Recipient ────────────────────────────────────────────────────────────

    selectRecipient(recipient) {
      this.toEmail = recipient.direct_address;
      this.toDisplayName = recipient.display_name;
      this.showRecipientPicker = false;
    },

    clearRecipient() {
      this.toEmail = '';
      this.toDisplayName = '';
    },

    // ── Backdrop / misc ─────────────────────────────────────────────────────

    handleBackdropClick() {
      if (this.body.trim() || this.attachments.length) return; // let parent handle if dirty
      this.$emit('update:modelValue', false);
    },

    clearPatient() {
      this.selectedPatientId = null;
      this.selectedPatientName = '';
      this.patientQuery = '';
      this.patientResults = [];
    },

    onPatientInput() {
      clearTimeout(this.patientTimer);
      const q = this.patientQuery.trim();
      if (!q) { this.patientResults = []; return; }
      this.patientTimer = setTimeout(() => this.searchPatients(q), 300);
    },

    async searchPatients(query) {
      this.patientLoading = true;
      try {
        const { data } = await axios.get('/api/entities/', {
          params: { entity_type_name: 'Patient', name: query },
        });
        const raw = Array.isArray(data) ? data : (data.results ?? []);
        const seen = new Set();
        this.patientResults = raw.filter((e) => {
          const key = e.identifiers?.BENE_MBI_ID ?? `name:${e.name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      } catch (e) {
        console.error('[ComposeMessageModal] patient search failed:', e?.message);
      } finally {
        this.patientLoading = false;
      }
    },

    selectPatient(entity) {
      this.selectedPatientId = entity.id;
      this.selectedPatientName = entity.name;
      this.patientQuery = '';
      this.patientResults = [];
    },

    onAttachmentChange(e) {
      this.attachments = [...this.attachments, ...Array.from(e.target.files)];
      e.target.value = '';
    },

    async submit() {
      this.error = '';
      this.sending = true;
      try {
        const attachmentData = await Promise.all(
          this.attachments.map(async (file) => ({
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            content: await readAsBase64(file),
          }))
        );
        const messagePayload = {
          recipient_emails: [this.toEmail.trim()],
          subject: this.subject.trim(),
          textMessage: this.body.trim(),
          entity_id: this.effectivePatientId ?? null,
          attachments: attachmentData,
        };
        const result = this.onSend
          ? await this.onSend(messagePayload)
          : await messagingApi.sendMessage(messagePayload);
        const message_id = result?.message_id || result?.provider_message?.external_id;
        if (!this.onSend && message_id && this.effectivePatientId) {
          await messagingApi.linkMessageEntity(message_id, { entity_id: this.effectivePatientId });
        }
        this.$emit('update:modelValue', false);
        this.$emit('sent', result);
      } catch (e) {
        this.error = messagingApi.errorMessage(e);
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
